import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp, Plus } from 'lucide-react';
import GuardianBadge from './GuardianBadge';
import ChildChapterPrompts from './ChildChapterPrompts';
import { getChapterPrompts } from '../../../services/childChaptersAPI';

/**
 * A single chapter card with expand/collapse for prompts, story count,
 * "+ Add Story" button, and urgency indicators.
 *
 * Props:
 *   chapter   - { id, title, emoji, description, author_voice, story_count, index }
 *   memberId  - family member UUID
 *   isUrgent  - if true show a gentle urgency indicator (e.g. for newborns)
 */
function ChildChapterCard({ chapter, memberId, isUrgent }) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [prompts, setPrompts] = useState([]);
  const [promptsLoaded, setPromptsLoaded] = useState(false);
  const [loadingPrompts, setLoadingPrompts] = useState(false);

  const storyCount = chapter.story_count || 0;

  const handleToggle = useCallback(async () => {
    const willExpand = !expanded;
    setExpanded(willExpand);

    if (willExpand && !promptsLoaded) {
      setLoadingPrompts(true);
      try {
        const bandKey = chapter.band_key || 'infant';
        const chapterIndex = chapter.number || chapter.chapter_index || 1;
        const data = await getChapterPrompts(memberId, bandKey, chapterIndex);
        setPrompts(Array.isArray(data) ? data : data.results || []);
        setPromptsLoaded(true);
      } catch (err) {
        console.error('Error loading prompts:', err);
      } finally {
        setLoadingPrompts(false);
      }
    }
  }, [expanded, promptsLoaded, memberId, chapter.id, chapter.band_key, chapter.number, chapter.chapter_index]);

  return (
    <div
      className={`rounded-xl border bg-white shadow-sm transition-shadow hover:shadow-md ${
        isUrgent ? 'border-[#E65100]/40 ring-1 ring-[#E65100]/20' : 'border-gray-200'
      }`}
    >
      {/* Header — always visible */}
      <button
        onClick={handleToggle}
        className="w-full text-left px-4 sm:px-5 py-4 flex items-center gap-3"
      >
        <span className="text-2xl flex-shrink-0">{chapter.emoji || '\uD83D\uDCD6'}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-serif text-lg font-semibold text-[#556B2F] truncate">
              {chapter.title}
            </h3>
            <GuardianBadge authorVoice={chapter.author_voice} />
            {isUrgent && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[#E65100]/10 text-[#E65100]">
                Start here
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500 mt-0.5 line-clamp-1">{chapter.description}</p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {storyCount > 0 && (
            <span className="text-sm text-[#556B2F]/70 font-medium">
              {storyCount} {storyCount === 1 ? 'story' : 'stories'}
            </span>
          )}
          {expanded ? (
            <ChevronUp size={20} className="text-gray-400" />
          ) : (
            <ChevronDown size={20} className="text-gray-400" />
          )}
        </div>
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="px-4 sm:px-5 pb-4 border-t border-gray-100 pt-3">
          {loadingPrompts ? (
            <p className="text-sm text-gray-400 py-2">Loading prompts...</p>
          ) : (
            <ChildChapterPrompts
              prompts={prompts}
              memberId={memberId}
              chapterId={chapter.id}
              note={chapter.note}
            />
          )}

          <div className="mt-4">
            <button
              onClick={() => navigate(`/tell-a-story?storyType=family_memory&chapter=${chapter.id}&person=${memberId}`)}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#556B2F] text-white text-sm font-medium hover:bg-[#4A5D23] transition-colors"
            >
              <Plus size={16} />
              Add Story
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChildChapterCard;
